import React from 'react'

import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  IconButton,
  Select,
  SelectOption,
  Stack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular, FaXmarkRegular } from '@enechain/ecloud-icons'
import { useErrorToast } from '@enechain/eposition-ui'

import { OrderSimulationSchema } from '~/pages/Simulation/schemas'

const MAX_MARGIN_SIMULATION_RELATIONS = 10

function createOptions(prefix: string, length: number): SelectOption[] {
  return Array.from({ length }, (_, i) => ({
    label: `${prefix}${i + 1}`,
    value: (i + 1).toString(),
  }))
}

export const MarginSimulationRelationForm: React.FC = () => {
  const { t } = useTranslation(['page', 'common'])
  const { control } = useFormContext<OrderSimulationSchema>()
  const orderPatterns = useWatch({
    control,
    name: 'orderPattern',
  })
  const watchedRelations = useWatch({
    control,
    name: 'marginSimulationRelation',
  })
  const { fields, update, append, remove } = useFieldArray({
    control,
    name: 'marginSimulationRelation',
  })
  const { showToast: showErrorToast } = useErrorToast({
    toastId: 'MarginSimulationRelationForm/error',
  })
  const options = createOptions(
    t('page:simulation.input_order'),
    orderPatterns?.length ?? 0,
  )

  const handleAddRelation = (): void => {
    if (fields.length >= MAX_MARGIN_SIMULATION_RELATIONS) {
      showErrorToast({
        toastMessage: t(
          'page:simulation.error_message.exceed_max_margin_simulation_relations',
          {
            value: MAX_MARGIN_SIMULATION_RELATIONS,
          },
        ),
      })
      return
    }
    append({ values: [] })
  }

  return (
    <Stack
      gap="4"
      paddingX="6"
      paddingY="4"
      border="1px solid"
      borderColor="Border.midEmphasis"
      borderRadius="radiiLg"
    >
      {fields.map((field, index) => (
        <HStack key={field.id} gap="4">
          <Text textStyle="headerXs">{index + 1}</Text>
          <Box flex={1}>
            <Select<SelectOption, true>
              isMulti
              placeholder={t('common:action.neutral.of_select', {
                value: t('page:simulation.input_order'),
              })}
              onChange={(item): void => {
                update(index, { values: item.map((i) => Number(i.value)) })
              }}
              options={options}
              selectedOptions={(watchedRelations[index]?.values ?? []).map(
                (selected: number) => ({
                  label: `${t('page:simulation.input_order')}${selected}`,
                  value: selected.toString(),
                }),
              )}
            />
          </Box>
          <IconButton
            size="xs"
            aria-label="delete row"
            backgroundColor="Surface.tertiary"
            onClick={() => remove(index)}
            rounded="full"
          >
            <FaXmarkRegular />
          </IconButton>
        </HStack>
      ))}
      <Box width="full" display="flex" justifyContent="center">
        <TertiaryButton
          height="32px"
          leftIcon={<FaPlusRegular />}
          onClick={handleAddRelation}
        >
          {t('page:simulation.add_margin_simulation_pattern')}
        </TertiaryButton>
      </Box>
    </Stack>
  )
}
